import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FormProgress } from '@hcc/types';
import { api } from '../../lib/api';

export type SubmissionStatus = 'pending' | 'awaiting_review' | 'changes_requested' | 'complete';
export interface SubmissionFileRef {
  name: string;
  key: string;
  content_type: string;
}
interface SubmissionBase {
  task_id: string;
  title: string;
  status: SubmissionStatus;
  updated_at: string | null;
}
export interface FormSubmissionEntry extends SubmissionBase {
  kind: 'form';
  form_id: string;
  form_version_id: string | null;
  state: 'not_started' | 'in_progress' | 'submitted';
  values: Record<string, unknown>;
  files: Record<string, SubmissionFileRef[]>;
  consent: boolean;
  progress: FormProgress | null;
  /** When a signed-PDF snapshot was last stored, else null. */
  signed_pdf_generated_at: string | null;
  /** When HR last corrected the answers, else null. */
  amended_at: string | null;
}
export interface DocumentSubmissionEntry extends SubmissionBase {
  kind: 'document';
  signed: boolean;
  /** When the inductee signed, else null; present before HR approval too. */
  signed_at: string | null;
}
export interface UploadSubmissionEntry extends SubmissionBase {
  kind: 'upload';
  files: SubmissionFileRef[];
}
export interface TodoSubmissionEntry extends SubmissionBase {
  kind: 'todo';
}
export type SubmissionEntry =
  | FormSubmissionEntry
  | DocumentSubmissionEntry
  | UploadSubmissionEntry
  | TodoSubmissionEntry;

export interface PersonSubmissions {
  person: {
    id: string;
    full_name: string;
    position: string | null;
    contract: string | null;
    start_date: string | null;
  };
  total: number;
  done: number;
  items: SubmissionEntry[];
}

/** Everything one person has submitted or started, for the Submissions page. */
export function usePersonSubmissions(personId: string) {
  return useQuery({
    queryKey: ['person-submissions', personId],
    queryFn: () =>
      api<PersonSubmissions>(`/api/admin/onboarding/people/${personId}/submissions`),
  });
}

/**
 * Generate and store the signed-PDF snapshot for one submitted form, so HR
 * keeps a copy identical to what the inductee downloads. Refreshes the
 * submissions view so the stored timestamp and download link appear.
 */
/**
 * HR corrects answers on a submitted form. Sends only the changed keys;
 * the server re-validates against the submitted version schema and writes
 * an OnboardingSubmissionAmended audit event. The signed PDF snapshot is
 * deliberately left as the inductee signed it.
 */
export function useAmendFormValues(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { formId: string; values: Record<string, unknown> }) =>
      api<{ ok: true; changed_keys: string[] }>(
        `/api/admin/onboarding/people/${personId}/forms/${input.formId}/values`,
        { method: 'PATCH', body: JSON.stringify({ values: input.values }) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['person-submissions', personId] });
    },
  });
}

export function useGenerateFormPdf(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (formId: string) =>
      api<{ ok: true; generated_at: string }>(
        `/api/admin/onboarding/people/${personId}/forms/${formId}/signed.pdf`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['person-submissions', personId] });
    },
  });
}
